import {
  Component,
  OnInit,
  EventEmitter,
  forwardRef,
  OnDestroy,
} from '@angular/core'
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'
import {combineLatest} from 'rxjs'
import {distinctUntilChanged, map, startWith, takeUntil} from 'rxjs/operators'
import {isEqual} from 'lodash'

@Component({
  selector: 'data-input',
  templateUrl: './data-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DataInputComponent
  implements OnInit, ControlValueAccessor, OnDestroy
{
  disabled: boolean = false

  form: FormGroup

  valueEmitter: EventEmitter<object> = new EventEmitter<object>()
  touchedEmitter: EventEmitter<void> = new EventEmitter<void>()

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    let values = this.fb.group({})
    values.valueChanges.pipe(takeUntil(this.end$)).subscribe((values) => {
      //trigger update on change
      this.valueEmitter.next(values)
    })
    this.form = this.fb.group({
      values: values,
      new: this.fb.group({
        key: this.fb.control(null),
        value: this.fb.control(null),
      }),
    })
  }

  end$ = new EventEmitter<void>()
  ngOnDestroy() {
    this.end$.next()
    this.end$.complete()
  }

  values: string[] = []
  control(name) {
    return this.form.get('values.' + name)
  }

  get newKey() {
    return this.form.get('new.key')
  }

  get newValue() {
    return this.form.get('new.value')
  }

  get errors() {
    return this.form.get('new').errors
  }

  setValue(key: string, value: string) {
    let control = this.form.get('values').get(key)
    if (value == null) {
      //allow empty values
      value = ''
    }
    if (!control) {
      let group = <FormGroup>this.form.get('values')
      let control = this.fb.control({value: value, disabled: this.disabled})
      group.addControl(key, control)
      this.values.push(key)
    } else {
      control.setValue(value)
    }
  }

  removeValue(key: string) {
    let control = this.form.get('values').get(key)
    if (control) {
      let group = <FormGroup>this.form.get('values')
      group.removeControl(key)
    }
    this.values.splice(this.values.indexOf(key), 1)
  }

  onAddKey() {
    let key = this.form.get('new.key').value
    let value = this.form.get('new.value').value
    if (key) {
      this.setValue(key.trim(), value)
      this.form.get('new').reset()
    } else {
      this.form.setErrors({new: 'key cannot be null'})
    }
  }

  writeValue(value: object) {
    let keys = []
    //set control value for all items in the value
    for (let key in value) {
      this.setValue(key, value[key])
      keys.push(key)
    }
    //remove all data items not in the value
    let group = <FormGroup>this.form.get('values')
    for (let key in group.controls) {
      if (!keys.includes(key)) {
        this.removeValue(key)
      }
    }
  }

  registerOnChange(fn) {
    combineLatest([
      this.form
        .get('values')
        .valueChanges.pipe(startWith(this.form.get('values').value)),
      this.form.get('new').valueChanges.pipe(startWith({})),
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        takeUntil(this.end$),
        map(([vals, newVals]) => {
          if (!!newVals.key && newVals.key.length) {
            return {...vals, ...{[newVals.key]: newVals.value || ''}}
          }
          return vals
        }),
      )
      .subscribe(fn)
  }

  registerOnTouched(_fn) {
    this.touchedEmitter.pipe(takeUntil(this.end$)).subscribe(() => {
      _fn()
    })
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled
    const controls = (<FormGroup>this.form.get('values')).controls
    for (let key in controls) {
      if (disabled) {
        controls[key].disable()
      } else {
        controls[key].enable()
      }
    }
  }
}
